import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CreditCard, ArrowLeft, CircleNotch as Loader2, ShieldCheck } from "@phosphor-icons/react";
import InAppBrowserWarning from "@/components/layout/InAppBrowserWarning";
import { DealerPlanSelector, DEALER_PLANS, type DealerPlanCode } from "@/components/dealers/DealerPlanSelector";

export interface DealerSignupData {
  email: string;
  password: string;
  name: string;
  phone: string;
  companyName: string;
  cui: string;
  address: string;
  country: string;
  marketingConsent: boolean;
  planCode?: DealerPlanCode;
  // Extended fields from /dealeri wizard (optional — legacy flow doesn't send them)
  firstName?: string;
  lastName?: string;
  legalEntityType?: string;
  showroomName?: string;
  subdomainAlias?: string;
  city?: string;
  postalCode?: string;
  jNumber?: string;
  companyDescription?: string;
  documentBase64?: string; // data URL, uploaded to storage post-signup
  documentFilename?: string;
}

interface Props {
  data: DealerSignupData;
  onBack: () => void;
  onSuccess: () => void;
}

export const PENDING_KEY = "dealer_signup_pending_v1";

export function DealerCardStep({ data, onBack }: Props) {
  const [planCode, setPlanCode] = useState<DealerPlanCode | null>(data.planCode ?? "standard");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  const startCheckout = async () => {
    if (startedRef.current || !planCode) return;
    startedRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const payload: DealerSignupData = { ...data, planCode };
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
      const { data: res, error: invErr } = await supabase.functions.invoke(
        "create-signup-checkout-session",
        { body: { email: data.email, returnOrigin: window.location.origin, plan_code: planCode } }
      );
      if (invErr || res?.error) {
        if (res?.error === "email_exists") {
          setError(res.message || "Există deja un cont cu acest email.");
        } else {
          setError(res?.error || invErr?.message || "Eroare la inițializare.");
        }
        sessionStorage.removeItem(PENDING_KEY);
        startedRef.current = false;
        return;
      }
      if (!res?.url) {
        setError("URL Stripe lipsă. Încearcă din nou.");
        sessionStorage.removeItem(PENDING_KEY);
        startedRef.current = false;
        return;
      }
      window.location.href = res.url;
    } catch (e: any) {
      setError(e?.message || "Eroare la inițializare.");
      sessionStorage.removeItem(PENDING_KEY);
      startedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const scrollSelfIntoView = () => {
    const doScroll = () => {
      try {
        const el = document.getElementById("dealer-card-step-anchor");
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo(0, Math.max(0, y));
        } else {
          window.scrollTo(0, 0);
        }
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } catch {
        window.scrollTo(0, 0);
      }
    };
    doScroll();
    requestAnimationFrame(doScroll);
    setTimeout(doScroll, 50);
    setTimeout(doScroll, 200);
  };

  useEffect(() => {
    scrollSelfIntoView();
  }, []);

  useEffect(() => {
    if (error) scrollSelfIntoView();
  }, [error]);

  const selectedPlan = DEALER_PLANS.find((p) => p.code === planCode);

  if (error) {
    return (
      <div id="dealer-card-step-anchor" className="space-y-4 py-4 scroll-mt-20">
        <InAppBrowserWarning blocking />
        <div className="rounded-lg border-2 border-destructive bg-destructive/10 p-4">
          <p className="text-sm font-semibold text-destructive mb-1">Nu am putut continua</p>
          <p className="text-sm text-destructive">{error}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} className="flex-1 h-11">
            <ArrowLeft className="mr-2 h-4 w-4" /> Înapoi
          </Button>
          <Button
            onClick={() => { startedRef.current = false; startCheckout(); }}
            className="flex-1 h-11"
          >
            <CreditCard className="mr-2 h-4 w-4" /> Reîncearcă
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div id="dealer-card-step-anchor" className="space-y-5 scroll-mt-20">
      <InAppBrowserWarning blocking />

      <DealerPlanSelector value={planCode} onChange={setPlanCode} />

      {selectedPlan && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            {selectedPlan.name} — {selectedPlan.price.toString().replace(".", ",")}€/lună
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Vei fi taxat <strong>{selectedPlan.price.toString().replace(".", ",")}€ imediat</strong> și apoi lunar.
            Anulare oricând, fără penalizare. Contul se creează după confirmarea plății.
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} className="flex-1 h-11" disabled={loading}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Înapoi
        </Button>
        <Button
          onClick={startCheckout}
          disabled={!planCode || loading}
          className="flex-1 h-11"
        >
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Se pregătește...</>
          ) : (
            <><CreditCard className="mr-2 h-4 w-4" /> Continuă spre plată</>
          )}
        </Button>
      </div>

      <p className="text-[11px] text-center text-muted-foreground">
        🔒 Plată securizată prin Stripe. Cardul tău nu este stocat pe serverele noastre.
      </p>
    </div>
  );
}
